'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetAccountProfileQuery } from '@/src/api/rtkQuery/profileApi'
import { TravellerData, TravellerDocDetail } from '@/src/types/traveller'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { transformTravellersResponse } from '@/src/utils/travellers/transformTravellersResponse'
import { buildTravellerDisplay } from '@/src/utils/travellers/buildTravellerDisplay'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

export interface TravellerListItemVm {
    id: number
    displayName: string
    initials: string
}

export interface TravellerDetailsVm {
    displayName: string
    initials: string
    gender: string
    email: string
    dateOfBirthValue: string
    phoneLabel: string
    documents: TravellerDocDetail[]
}

export const useAccountTravellers = () => {
    const { formatDate } = useFormatDate()
    const { data: rawTravellers, isLoading, isError, refetch } = useGetAccountProfileQuery({
        travellersRequired: true,
        docRequired: true,
        fetchControl: isCleartrip() ? 'MAIN' : 'ALL'
    })

    const travellers: TravellerData[] = useMemo(() => {
        if (!rawTravellers) return []
        return transformTravellersResponse(rawTravellers)
    }, [rawTravellers])

    const [activeTravellerId, setActiveTravellerId] = useState<number | null>(null)

    useEffect(() => {
        if (travellers.length === 0) {
            setActiveTravellerId(null)
            return
        }

        const hasActiveTraveller = activeTravellerId != null && travellers.some(item => item.id === activeTravellerId)
        if (!hasActiveTraveller) {
            setActiveTravellerId(travellers[0].id)
        }
    }, [travellers, activeTravellerId])

    const effectiveActiveTraveller: TravellerData | null = useMemo(() => {
        if (!travellers.length) return null

        if (activeTravellerId != null) {
            return travellers.find(item => item.id === activeTravellerId) || null
        }

        return travellers[0]
    }, [travellers, activeTravellerId])

    const travellersForView: TravellerListItemVm[] = useMemo(() => {
        return travellers.map(traveller => {
            const { displayName, initials } = buildTravellerDisplay({
                title: traveller.title,
                firstName: traveller.firstName,
                lastName: traveller.lastName
            })

            return { id: traveller.id, displayName, initials }
        })
    }, [travellers])

    const activeTravellerForView: TravellerDetailsVm | null = useMemo(() => {
        if (!effectiveActiveTraveller) return null

        const { displayName, initials } = buildTravellerDisplay({
            title: effectiveActiveTraveller.title,
            firstName: effectiveActiveTraveller.firstName,
            lastName: effectiveActiveTraveller.lastName
        })

        const phoneLabel = effectiveActiveTraveller.phone?.number ? `+${effectiveActiveTraveller.phone.countryCode} ${effectiveActiveTraveller.phone.number}` : '--'
        const dateOfBirthValue = effectiveActiveTraveller.dateOfBirth ? formatDate(effectiveActiveTraveller.dateOfBirth, 'DD MMMM YYYY') : '--'

        return {
            displayName,
            initials,
            gender: effectiveActiveTraveller.gender,
            email: effectiveActiveTraveller.email,
            dateOfBirthValue,
            phoneLabel,
            documents: effectiveActiveTraveller.documents
        }
    }, [effectiveActiveTraveller, formatDate])

    const onSelectTraveller = useCallback((id: number) => {
        setActiveTravellerId(id)
    }, [])

    return {
        isLoading,
        isError,
        refetch,
        travellersForView,
        activeTravellerForView,
        activeTravellerId,
        onSelectTraveller
    }
}

export default useAccountTravellers

